import XZoomBound from './x-zoom-bound';
import YZoomBound from './y-zoom-bound';
import { numberToUserDisplay, userEntryToNumber } from '@ebiz/pretty-num';

// Turn on debugging console logging by flipping boolean
const DEBUG = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debugLog(...args: any[]) {
  if (DEBUG) console.log(...args);
}

export interface ZoomLimits {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface ZoomRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ZoomPosition {
  x: number;
  y: number;
}

export interface ZoomTick {
  x: number;
  y: number;
  value: string;
}
/**
 * Class for handling zooming.
 *
 * This class determines the values at the corners of the zoom rectangle by
 * interpolating between adjacent ticks of the current graph, adding artificial
 * ticks at the current limits if they aren't already present.
 * (See x-zoom-bound and y-zoom-bound for the interpolation logic).
 * It returns the new values as the limits to apply to the zoomed graph.
 *
 * Everything needed for the zoom lifecycle is provided by the following three
 * method calls:
 * - startZoomSelect - Start the zoom, returning the point of selection start.
 *   Call from mouseDown.
 * - moveZoomSelect - Calculate movement in progress, returning the selection
 *   rectangle. Call from mouseMove.
 * - stopZoomSelect - Complete the zoom, returning the updated graph limits.
 *   Call from mouseUp.
 *
 * The class holds no state beyond the handling of a single zoom.
 * For repeated zooms, the results of the previous zoom are held in the
 * limits it returns, which can then be used as the basis for subsequent zooms.
 *
 * @module
 */
export default class GraphZoomModel {
  // All state is internal and not intended for external access
  xTicks?: ZoomTick[];
  yTicks?: ZoomTick[];
  limits?: ZoomLimits | null = null;
  frameBox: DOMRect | null = null;
  selectionStartPoint: ZoomPosition | null = null;
  selectionRectangle: ZoomRect | null = null;

  // Sequester all DOM interaction to a self-contained item we can stub in unit tests
  fromDom = {
    _clearScrollRanges: () => {
      window.getSelection()?.removeAllRanges(); // Should work on all modern browsers
    },
    _getCurrentPosition: (event: MouseEvent, frameBox: DOMRect): ZoomPosition => {
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      return {
        x: event.pageX - (frameBox.left + scrollX),
        y: event.pageY - (frameBox.top + scrollY),
      };
    },
    _getFrameBox: (element: Element): DOMRect => {
      return element.getBoundingClientRect();
    },
  };

  startZoomSelect(event: MouseEvent, element: Element, xticks: ZoomTick[], yticks: ZoomTick[], limits: ZoomLimits) {
    this.fromDom._clearScrollRanges();
    const frameBox = this.fromDom._getFrameBox(element);
    const selectionStartPoint = this.fromDom._getCurrentPosition(event, frameBox);
    if (
      selectionStartPoint.x >= 0 &&
      selectionStartPoint.y >= 0 &&
      selectionStartPoint.x <= frameBox.width &&
      selectionStartPoint.y <= frameBox.height
    ) {
      this.selectionStartPoint = selectionStartPoint;
      this.frameBox = frameBox;
      this.limits = limits;
      this.xTicks = xticks;
      this.yTicks = yticks;
      return selectionStartPoint;
    } else {
      return;
    }
  }

  moveZoomSelect(event: MouseEvent): ZoomRect | undefined {
    if (this.frameBox && this.selectionStartPoint) {
      const currentPosition = this.fromDom._getCurrentPosition(event, this.frameBox);
      const selectionRectangle = this._getSelectionRectangle(this.selectionStartPoint, currentPosition);
      if (selectionRectangle) {
        if (selectionRectangle.width > this.frameBox.width - selectionRectangle.x) {
          selectionRectangle.width = this.frameBox.width - selectionRectangle.x;
        } else if (selectionRectangle.width < 0) {
          selectionRectangle.width = 0;
        }

        if (selectionRectangle.height > this.frameBox.height - selectionRectangle.y) {
          selectionRectangle.height = this.frameBox.height - selectionRectangle.y;
        } else if (selectionRectangle.height < 0) {
          selectionRectangle.height = 0;
        }
        this.selectionRectangle = selectionRectangle;
        return selectionRectangle;
      }
    }
  }

  stopZoomSelect(minWidth: number, minHeight: number) {
    this.selectionStartPoint = null;
    if (
      !this.selectionRectangle ||
      this.selectionRectangle.width < minWidth ||
      this.selectionRectangle.height < minHeight
    ) {
      debugLog('Zoom: Was click, not drag');
      return;
    }

    if (!this.frameBox || !this.xTicks || !this.yTicks) {
      debugLog('Zoom: Unexpected missing data');
      return;
    }

    debugLog('Zoom: inputs', this.limits, this.frameBox, this.xTicks, this.yTicks);

    const originalRectangle = this.selectionRectangle;
    const limitedRectangle: ZoomRect = {
      x: originalRectangle.x,
      y: originalRectangle.y,
      width:
        originalRectangle.x + originalRectangle.width > this.frameBox.width
          ? this.frameBox.width - originalRectangle.x
          : originalRectangle.width,
      height:
        originalRectangle.y + originalRectangle.height > this.frameBox.height
          ? this.frameBox.height - originalRectangle.y
          : originalRectangle.height,
    };

    debugLog('Zoom: rectangle', originalRectangle, limitedRectangle);

    const xTicksWithLimits = this._xTicksWithLimits();
    const yTicksWithLimits = this._yTicksWithLimits();

    debugLog('Zoom: ticksWithLimits', xTicksWithLimits, yTicksWithLimits);

    const startX = new XZoomBound(limitedRectangle, xTicksWithLimits, false);
    const endX = new XZoomBound(limitedRectangle, xTicksWithLimits, true);
    const startY = new YZoomBound(limitedRectangle, yTicksWithLimits, false);
    const endY = new YZoomBound(limitedRectangle, yTicksWithLimits, true);

    const zoomLimits = {
      minX: userEntryToNumber(startX?.displayValue),
      maxX: userEntryToNumber(endX?.displayValue),
      minY: userEntryToNumber(endY?.displayValue),
      maxY: userEntryToNumber(startY?.displayValue),
    };
    debugLog('Zoom: results', zoomLimits);
    this.selectionRectangle = null;
    return zoomLimits;
  }

  // Internal utility methods
  _xTicksWithLimits(): ZoomTick[] {
    if (!this.frameBox || !this.xTicks || !this.limits) {
      debugLog('Zoom: Unexpected missing data');
      return [];
    }
    const frameBbox = this.frameBox;
    const openingScaleValue = {
      value: numberToUserDisplay(this.limits.minX, 'Engineering', 5),
      x: 0,
      y: frameBbox.height,
    };
    const closingScaleValue = {
      value: numberToUserDisplay(this.limits.maxX, 'Engineering', 5),
      x: frameBbox.width,
      y: frameBbox.height,
    };
    // Add edges but avoid adding duplicates
    let result: ZoomTick[] = [];
    if (this.xTicks[0] && openingScaleValue.x !== this.xTicks[0].x) {
      result.push(openingScaleValue);
    }
    result = result.concat(this.xTicks);
    const last = this.xTicks[this.xTicks.length - 1];
    if (last && closingScaleValue.x !== last.x) {
      result.push(closingScaleValue);
    }
    return result;
  }

  _yTicksWithLimits(): ZoomTick[] {
    if (!this.frameBox || !this.yTicks || !this.limits) {
      debugLog('Zoom: Unexpected missing data');
      return [];
    }

    const frameBbox = this.frameBox;
    const openingScaleValue = {
      value: numberToUserDisplay(this.limits.maxY, 'Standard', 5),
      x: 0,
      y: 0,
    };
    const closingScaleValue = {
      value: numberToUserDisplay(this.limits.minY, 'Standard', 5),
      x: 0,
      y: frameBbox.height,
    };
    // Add edges but avoid adding duplicates
    let result: ZoomTick[] = [];
    if (this.yTicks[0] && closingScaleValue.y !== this.yTicks[0].y) {
      result.push(closingScaleValue);
    }
    result = result.concat(this.yTicks);
    const last = this.yTicks[this.yTicks.length - 1];
    if (last && openingScaleValue.y !== last.y) {
      result.push(openingScaleValue);
    }
    return result;
  }
  // Pure function - consumes two positions and returns fresh selectionRectangle
  _getSelectionRectangle(startPosition: ZoomPosition, endPosition: ZoomPosition) {
    let result = null;
    if (endPosition.x > startPosition.x && endPosition.y > startPosition.y) {
      result = {
        x: startPosition.x,
        y: startPosition.y,
        width: endPosition.x - startPosition.x,
        height: endPosition.y - startPosition.y,
      };
    } else if (endPosition.x < startPosition.x && endPosition.y > startPosition.y) {
      result = {
        x: endPosition.x,
        y: startPosition.y,
        width: startPosition.x - endPosition.x,
        height: endPosition.y - startPosition.y,
      };
    } else if (endPosition.x > startPosition.x && endPosition.y < startPosition.y) {
      result = {
        x: startPosition.x,
        y: endPosition.y,
        width: endPosition.x - startPosition.x,
        height: startPosition.y - endPosition.y,
      };
    } else if (endPosition.x < startPosition.x && endPosition.y < startPosition.y) {
      result = {
        x: endPosition.x,
        y: endPosition.y,
        width: startPosition.x - endPosition.x,
        height: startPosition.y - endPosition.y,
      };
    }
    return result;
  }
}
